import { useEnvironmentalAspect } from 'charts/environmentalAspect';
import { useHsEffect } from 'charts/hsEffect';
import { useHsHazardType } from 'charts/hazardType';
import { useImpact } from 'charts/impact';
import { useOriginator } from 'charts/originator';
import { useRootCause } from 'charts/rootCause';
import { useWorkType } from 'charts/workType';
import { useMemo } from 'react';
import type { ChartFiltersUnion } from 'shared/types/analytics';
import type { ChartId } from '../../catalog/chartCatalog';
import type { ChartResult } from '../../catalog/types';
import type { CatalogIssueChartId } from '../../constants/catalogIssueCharts';
import { useCatalogChartBundle } from '../useCatalogChartBundle';
import type { ChartRuntime } from 'views/analytics/types';

export type IssueChartRuntime = Readonly<{
  results: Readonly<Partial<Record<ChartId, ChartResult>>>;
  runtimeById: Readonly<
    Partial<Record<CatalogIssueChartId, ChartRuntime<ChartFiltersUnion>>>
  >;
}>;

export function useIssueChartResultsInternal(): IssueChartRuntime {
  const originator = useCatalogChartBundle('originator', useOriginator());
  const rootCause = useCatalogChartBundle('rootCause', useRootCause());
  const impact = useCatalogChartBundle('impact', useImpact());
  const environmentalAspect = useCatalogChartBundle(
    'environmentalAspect',
    useEnvironmentalAspect()
  );
  const workType = useCatalogChartBundle('workType', useWorkType());
  const hsEffect = useCatalogChartBundle('hsEffect', useHsEffect());
  const hazardType = useCatalogChartBundle('hazardType', useHsHazardType());

  return useMemo(
    (): IssueChartRuntime => ({
      results: {
        originator: originator.result,
        rootCause: rootCause.result,
        impact: impact.result,
        environmentalAspect: environmentalAspect.result,
        workType: workType.result,
        hsEffect: hsEffect.result,
        hazardType: hazardType.result,
      },
      runtimeById: {
        originator: originator.runtime,
        rootCause: rootCause.runtime,
        impact: impact.runtime,
        environmentalAspect: environmentalAspect.runtime,
        workType: workType.runtime,
        hsEffect: hsEffect.runtime,
        hazardType: hazardType.runtime,
      },
    }),
    [
      originator.result,
      originator.runtime,
      rootCause.result,
      rootCause.runtime,
      impact.result,
      impact.runtime,
      environmentalAspect.result,
      environmentalAspect.runtime,
      workType.result,
      workType.runtime,
      hsEffect.result,
      hsEffect.runtime,
      hazardType.result,
      hazardType.runtime,
    ]
  );
}
